import { useEffect, useState } from 'react';
import type { Editor } from '../editor';
import { ENTITY_LABEL, type CadEntity } from '../model/entity';
import { dist, polygonArea, polylineLength } from '../model/geom';
import { formatLength, toDisplay, toSquareMeters } from '../model/units';
import type { Layer } from '../model/document';
import {
  addLayer,
  countEntitiesOnLayer,
  deleteLayer,
  renameLayerCode,
  updateLayer,
  type LayerEditResult,
} from '../ops/layerOps';
import { BlockLibrary } from './BlockLibrary';
import { VersionPanel } from './VersionPanel';
import { ReviewPanel } from './ReviewPanel';
import { RulePanel } from './RulePanel';
import { RegisterBlockModal } from './RegisterBlockModal';
import type { VersionStatus } from '../../data/types';
import { isAdmin, type AppUser } from '../../boot/session';
import { OneUI } from '../../shell/oneUi';

type Tab = 'blocks' | 'layers' | 'attach' | 'props' | 'rules' | 'versions' | 'review';

/** 도면·버전 문맥. 편집기를 도면 없이(테스트 등) 띄울 수도 있어 선택값이다. */
export interface PanelContext {
  projectId: string;
  drawingId: string;
  drawingName: string;
  versionId: string;
  branchId: string;
  status: VersionStatus;
  user: AppUser;
  onNavigateVersion: (versionId: string) => void;
  onStatusChange: (next: VersionStatus) => void;
}

/** 오른쪽 패널 — 블록 · 레이어 · 속성 · 버전 (P2-15 / P4-01 / P4-05 / P4-08) */
export function SidePanel({ editor, context }: { editor: Editor; context?: PanelContext }) {
  const [tab, setTab] = useState<Tab>('blocks');
  const [, setTick] = useState(0);
  useEffect(() => editor.subscribe(() => setTick((t) => t + 1)), [editor]);
  useEffect(() => editor.scene.subscribe(() => setTick((t) => t + 1)), [editor]);

  return (
    <aside className="cad-panel">
      <div className="cad-panel__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'blocks'}
          className="cad-panel__tab"
          onClick={() => setTab('blocks')}
        >
          블록
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'layers'}
          className="cad-panel__tab"
          onClick={() => setTab('layers')}
        >
          레이어
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'attach'}
          className="cad-panel__tab"
          onClick={() => setTab('attach')}
        >
          첨부 {editor.scene.doc.underlays.length > 0 && `(${editor.scene.doc.underlays.length})`}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'props'}
          className="cad-panel__tab"
          onClick={() => setTab('props')}
        >
          속성 {editor.selection.size > 0 && `(${editor.selection.size})`}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'rules'}
          className="cad-panel__tab"
          onClick={() => setTab('rules')}
        >
          검증
        </button>
        {context && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'versions'}
            className="cad-panel__tab"
            onClick={() => setTab('versions')}
          >
            버전
          </button>
        )}
        {context && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'review'}
            className="cad-panel__tab"
            onClick={() => setTab('review')}
          >
            검토
          </button>
        )}
      </div>

      <div className="cad-panel__body">
        {tab === 'blocks' && <BlockLibrary editor={editor} />}
        {tab === 'layers' && <LayerList editor={editor} />}
        {tab === 'attach' && <AttachList editor={editor} />}
        {tab === 'props' && <Properties editor={editor} user={context?.user} />}
        {tab === 'rules' && <RulePanel editor={editor} />}
        {tab === 'review' && context && (
          <ReviewPanel
            editor={editor}
            projectId={context.projectId}
            drawingId={context.drawingId}
            drawingName={context.drawingName}
            versionId={context.versionId}
            status={context.status}
            user={context.user}
            onStatusChange={context.onStatusChange}
          />
        )}
        {tab === 'versions' && context && (
          <VersionPanel
            drawingId={context.drawingId}
            currentVersionId={context.versionId}
            currentBranchId={context.branchId}
            onNavigate={context.onNavigateVersion}
          />
        )}
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ 레이어 */

function LayerList({ editor }: { editor: Editor }) {
  const doc = editor.scene.doc;
  const layers = [...doc.layers].sort((a, b) => a.sortOrder - b.sortOrder);

  const [message, setMessage] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  /** 레이어 편집 결과를 한 곳에서 적용한다 — 실패 이유도 여기서만 띄운다 */
  function apply(result: LayerEditResult): boolean {
    if (result.error) {
      setMessage(result.error);
      return false;
    }
    if (result.ops.length > 0) editor.scene.commit('LAYER', result.ops);
    setMessage(result.notice ?? null);
    return true;
  }

  function patch(code: string, change: Partial<Layer>) {
    apply(updateLayer(doc, code, change));
  }

  return (
    <div className="layer-list">
      <p className="cad-panel__hint">
        현재 레이어를 고르면 새로 그리는 객체가 그 레이어에 놓입니다.
      </p>

      {message && (
        <p className="cad-panel__notice" role="status">
          {message}
        </p>
      )}

      {layers.map((l) => {
        const current = editor.currentLayer === l.code;
        const count = countEntitiesOnLayer(doc, l.code);

        return (
          <div className={current ? 'layer-row layer-row--current' : 'layer-row'} key={l.code}>
            <button
              type="button"
              className="layer-row__pick"
              title="현재 레이어로 지정"
              aria-pressed={current}
              onClick={() => {
                editor.currentLayer = l.code;
                editor.write(`현재 레이어: ${l.code}`);
                setMessage(null);
              }}
            >
              <span className="layer-row__swatch" style={{ background: l.color }} aria-hidden="true" />
              <span className="layer-row__code">{l.code}</span>
              <span className="layer-row__name">{l.name}</span>
              {count > 0 && <span className="layer-row__count">{count}</span>}
            </button>

            <button
              type="button"
              className="layer-row__icon"
              aria-pressed={l.visible}
              title={l.visible ? '숨기기' : '보이기'}
              onClick={() => patch(l.code, { visible: !l.visible })}
            >
              {l.visible ? '👁' : '🚫'}
            </button>

            <button
              type="button"
              className="layer-row__icon"
              aria-pressed={l.locked}
              title={l.locked ? '잠금 해제' : '잠그기'}
              onClick={() => patch(l.code, { locked: !l.locked })}
            >
              {l.locked ? '🔒' : '🔓'}
            </button>

            <button
              type="button"
              className="layer-row__icon"
              title="레이어 수정"
              onClick={() => {
                setEditing(editing === l.code ? null : l.code);
                setDeleting(null);
                setMessage(null);
              }}
            >
              ✏️
            </button>

            <button
              type="button"
              className="layer-row__icon"
              title="레이어 삭제"
              onClick={() => {
                setDeleting(deleting === l.code ? null : l.code);
                setEditing(null);
                setMessage(null);
              }}
            >
              🗑
            </button>

            {editing === l.code && (
              <LayerEditForm
                layer={l}
                onCancel={() => setEditing(null)}
                onSave={(next) => {
                  // 코드 변경은 객체까지 함께 옮겨야 하므로 별도 함수를 쓴다
                  if (next.code !== l.code) {
                    if (!apply(renameLayerCode(doc, l.code, next.code))) return;
                    if (editor.currentLayer === l.code) editor.currentLayer = next.code;
                  }
                  const code = next.code !== l.code ? next.code : l.code;
                  apply(updateLayer(editor.scene.doc, code, { name: next.name, color: next.color }));
                  setEditing(null);
                }}
              />
            )}

            {deleting === l.code && (
              <LayerDeleteForm
                layer={l}
                count={count}
                others={layers.filter((x) => x.code !== l.code)}
                onCancel={() => setDeleting(null)}
                onConfirm={(moveTo) => {
                  if (!apply(deleteLayer(doc, l.code, moveTo ? { moveTo } : {}))) return;
                  if (editor.currentLayer === l.code) {
                    editor.currentLayer = moveTo ?? editor.scene.doc.layers[0]!.code;
                  }
                  setDeleting(null);
                }}
              />
            )}
          </div>
        );
      })}

      {adding ? (
        <LayerEditForm
          layer={null}
          onCancel={() => setAdding(false)}
          onSave={(next) => {
            if (apply(addLayer(doc, next))) setAdding(false);
          }}
        />
      ) : (
        <button
          type="button"
          className="btn btn--ghost btn--block"
          onClick={() => {
            setAdding(true);
            setMessage(null);
          }}
        >
          + 레이어 추가
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ 첨부(Attach) */

/**
 * 부착(Attach) 이미지 목록 — 신규 요청.
 * 편집·선택 대상이 아니므로(document.ts UnderlayRef 주석 참고) 여기서 할 수 있는 건
 * 보이기/숨기기와 삭제뿐이다. 추가는 파일 메뉴의 "부착" 버튼에서 한다.
 */
function AttachList({ editor }: { editor: Editor }) {
  const doc = editor.scene.doc;
  const underlays = doc.underlays;

  function commit(next: typeof underlays, command: string) {
    editor.scene.commit(command, [
      {
        kind: 'meta',
        command,
        before: { underlays: doc.underlays.map((u) => ({ ...u })) },
        after: { underlays: next },
      },
    ]);
  }

  return (
    <div className="layer-list">
      <p className="cad-panel__hint">
        PDF 를 참조용으로 붙인 배경 이미지입니다. 편집·선택 대상이 아닙니다 — 파일 메뉴의 "부착"
        버튼으로 추가합니다.
      </p>

      {underlays.length === 0 && <p className="cad-panel__hint">부착된 이미지가 없습니다.</p>}

      {underlays.map((u, i) => (
        <div className="layer-row" key={u.id}>
          <span className="layer-row__name" style={{ flex: 1 }}>
            부착 이미지 {i + 1} ({Math.round(toDisplay(u.widthUm, 'mm'))}×
            {Math.round(toDisplay(u.heightUm, 'mm'))}mm)
          </span>
          <button
            type="button"
            className="layer-row__icon"
            aria-pressed={u.visible}
            title={u.visible ? '숨기기' : '보이기'}
            onClick={() =>
              commit(
                underlays.map((x) => (x.id === u.id ? { ...x, visible: !x.visible } : x)),
                'ATTACH_TOGGLE'
              )
            }
          >
            {u.visible ? '👁' : '🚫'}
          </button>
          <button
            type="button"
            className="layer-row__icon"
            title="삭제"
            onClick={async () => {
              const ok = await OneUI.confirm('이 부착 이미지를 지울까요?', {
                okText: '지우기',
                danger: true,
              });
              if (ok) {
                commit(
                  underlays.filter((x) => x.id !== u.id),
                  'ATTACH_REMOVE'
                );
              }
            }}
          >
            🗑
          </button>
        </div>
      ))}
    </div>
  );
}

interface LayerDraft {
  code: string;
  name: string;
  color: string;
}

function LayerEditForm({
  layer,
  onSave,
  onCancel,
}: {
  layer: Layer | null;
  onSave: (draft: LayerDraft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<LayerDraft>({
    code: layer?.code ?? '',
    name: layer?.name ?? '',
    color: layer?.color ?? '#D9D9D9',
  });

  return (
    <form
      className="layer-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
    >
      <label className="param-row">
        <span className="param-row__label">코드</span>
        <input
          className="input"
          value={draft.code}
          autoFocus
          placeholder="F-FIXTURE"
          onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
        />
      </label>
      <label className="param-row">
        <span className="param-row__label">이름</span>
        <input
          className="input"
          value={draft.name}
          placeholder="매대·집기"
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        />
      </label>
      <label className="param-row">
        <span className="param-row__label">색</span>
        <input
          className="input input--color"
          type="color"
          value={draft.color}
          onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
        />
      </label>
      <div className="layer-form__actions">
        <button type="submit" className="btn btn--primary btn--sm">
          {layer ? '저장' : '추가'}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          취소
        </button>
      </div>
    </form>
  );
}

/**
 * 삭제 확인.
 *
 * ★ 객체가 있는 레이어는 옮길 곳을 고르기 전에는 지울 수 없다.
 *   말없이 지우면 사용자는 도면이 손상된 걸 한참 뒤에 발견한다.
 */
function LayerDeleteForm({
  layer,
  count,
  others,
  onConfirm,
  onCancel,
}: {
  layer: Layer;
  count: number;
  others: Layer[];
  onConfirm: (moveTo?: string) => void;
  onCancel: () => void;
}) {
  const [moveTo, setMoveTo] = useState(others[0]?.code ?? '');

  return (
    <div className="layer-form">
      {count > 0 ? (
        <>
          <p className="cad-panel__hint">
            <strong>{layer.code}</strong> 에 객체 {count}개가 있습니다. 어디로 옮길까요?
          </p>
          <label className="param-row">
            <span className="param-row__label">옮길 레이어</span>
            <select className="select" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              {others.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.code} · {l.name}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : (
        <p className="cad-panel__hint">
          <strong>{layer.code}</strong> 를 지웁니다. (빈 레이어)
        </p>
      )}
      <div className="layer-form__actions">
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={() => onConfirm(count > 0 ? moveTo : undefined)}
        >
          삭제
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- 속성 */

function Properties({ editor, user }: { editor: Editor; user?: AppUser }) {
  const [registering, setRegistering] = useState(false);
  const canRegisterBlock = !!user && isAdmin(user);

  const ids = [...editor.selection];
  const unit = editor.scene.doc.displayUnit;

  if (ids.length === 0) {
    return <p className="cad-panel__hint">선택된 객체가 없습니다.</p>;
  }

  const entities = ids.map((id) => editor.scene.get(id)).filter(Boolean) as CadEntity[];
  if (entities.length === 0) return <p className="cad-panel__hint">선택된 객체가 없습니다.</p>;

  const registerButton = canRegisterBlock && (
    <button type="button" className="btn btn--sm" onClick={() => setRegistering(true)}>
      선택한 도형을 블록으로 등록하기
    </button>
  );

  // 여러 개 선택 — 집계만 보여준다
  if (entities.length > 1) {
    const byType = new Map<string, number>();
    let totalLength = 0;
    for (const e of entities) {
      byType.set(e.type, (byType.get(e.type) ?? 0) + 1);
      totalLength += measureLength(e);
    }
    return (
      <div className="prop-list">
        <Row label="선택" value={`${entities.length}개`} />
        {[...byType].map(([t, n]) => (
          <Row key={t} label={ENTITY_LABEL[t as keyof typeof ENTITY_LABEL] ?? t} value={`${n}개`} />
        ))}
        {totalLength > 0 && (
          <Row label="총 길이" value={`${formatLength(Math.round(totalLength), unit)} ${unit}`} />
        )}
        <p className="cad-panel__hint">
          여러 개를 선택했습니다. 레이어를 바꾸려면 아래에서 고르세요.
        </p>
        <LayerChanger editor={editor} entities={entities} />
        {registerButton}
        {registering && (
          <RegisterBlockModal
            editor={editor}
            entities={entities}
            onClose={() => setRegistering(false)}
          />
        )}
      </div>
    );
  }

  const e = entities[0]!;
  return (
    <div className="prop-list">
      <Row label="종류" value={ENTITY_LABEL[e.type]} />
      <Row label="레이어" value={e.layer} />
      {e.type === 'line' && (
        <>
          <Row label="시작" value={`${formatLength(e.a.x, unit)}, ${formatLength(e.a.y, unit)}`} />
          <Row label="끝" value={`${formatLength(e.b.x, unit)}, ${formatLength(e.b.y, unit)}`} />
          <Row label="길이" value={`${formatLength(Math.round(dist(e.a, e.b)), unit)} ${unit}`} />
        </>
      )}
      {e.type === 'polyline' && (
        <>
          <Row label="정점" value={`${e.points.length}개`} />
          <Row label="닫힘" value={e.closed ? '예' : '아니오'} />
          <Row
            label="길이"
            value={`${formatLength(Math.round(polylineLength(e.points, e.closed)), unit)} ${unit}`}
          />
          {e.closed && (
            <Row label="면적" value={`${toSquareMeters(polygonArea(e.points)).toFixed(2)} ㎡`} />
          )}
        </>
      )}
      {e.type === 'circle' && (
        <>
          <Row label="중심" value={`${formatLength(e.center.x, unit)}, ${formatLength(e.center.y, unit)}`} />
          <Row label="반지름" value={`${formatLength(e.radius, unit)} ${unit}`} />
        </>
      )}
      {e.type === 'arc' && (
        <>
          <Row label="중심" value={`${formatLength(e.center.x, unit)}, ${formatLength(e.center.y, unit)}`} />
          <Row label="반지름" value={`${formatLength(e.radius, unit)} ${unit}`} />
        </>
      )}
      {e.type === 'text' && (
        <>
          <Row label="내용" value={e.text} />
          <Row label="높이" value={`${formatLength(e.height, unit)} ${unit}`} />
        </>
      )}
      {e.type === 'blockref' && (
        <>
          <Row label="블록" value={e.blockCode} />
          <Row label="배율" value={String(e.scale)} />
          {Object.entries(e.attributes).map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
        </>
      )}

      <LayerChanger editor={editor} entities={entities} />
      {e.type !== 'blockref' && registerButton}
      {registering && (
        <RegisterBlockModal editor={editor} entities={entities} onClose={() => setRegistering(false)} />
      )}
    </div>
  );
}

function measureLength(e: CadEntity): number {
  switch (e.type) {
    case 'line':
      return dist(e.a, e.b);
    case 'polyline':
      return polylineLength(e.points, e.closed);
    case 'circle':
      return 2 * Math.PI * e.radius;
    default:
      return 0;
  }
}

function LayerChanger({ editor, entities }: { editor: Editor; entities: CadEntity[] }) {
  return (
    <label className="prop-row prop-row--control">
      <span className="prop-row__label">레이어 변경</span>
      <select
        className="select"
        value={entities.length === 1 ? entities[0]!.layer : ''}
        onChange={(ev) => {
          const layer = ev.target.value;
          if (!layer) return;
          editor.scene.commit('CHPROP', [
            {
              kind: 'update',
              command: 'CHPROP',
              before: entities,
              after: entities.map((e) => ({ ...e, layer })),
            },
          ]);
        }}
      >
        {entities.length > 1 && <option value="">— 선택 —</option>}
        {editor.scene.doc.layers.map((l) => (
          <option key={l.code} value={l.code}>
            {l.code} · {l.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="prop-row">
      <span className="prop-row__label">{label}</span>
      <span className="prop-row__value">{value}</span>
    </div>
  );
}
